import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useMessage} from '../../../../hooks/message';
import {useQuestion} from '../../../../hooks/question';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useRPC} from '../../../../hooks/rpc';
import {MediaInputModel} from '../../../../store/control/state';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useSelector} from 'react-redux';
import {SelectItem} from '../../../../components/Select';
import {BridgeDeviceModel} from '../../../../types/bridge-device';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {ControlModel, ControlRenderType} from '../../../../types/control';
import {useSetSelector} from '../../../../hooks/set-selector';
import {SettingReadOnlyRowComponent} from '../../../../components/Settings/SettingReadOnlyRowComponent';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {useMap} from '../../../../hooks/map';
import {useLocalization} from "../../../../hooks/localization";
import {useCurrentGateway} from '../../../../hooks/current-gateway';
import './index.scss'
import { onClickHandler } from '../../../../utils/onclick';

const BACK_URL = '/settings/media';

export const MediaInputEditScreen = memo(function MediaInputEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const {success, failure} = useMessage();
    const question = useQuestion();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [rpc] = useRPC();
    const history = useHistory();

    const mediaInputList = useSelector(state => state.control.mediaInputList);
    const bridgeDeviceSet = useSetSelector(state => state.control.bridgeDeviceTypeList);
    const bridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMap(
        useFilterSelector(
            state => state.control.bridgeDeviceList,
            bridgeDevice => bridgeDeviceSet[bridgeDevice.type_id]?.is_media_device,
            [bridgeDeviceSet],
        ),
        bridgeDevice => ({
            value: bridgeDevice,
            label: bridgeDevice.name,
        }));
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList);
    const isInstallationMode = useCurrentGateway()

    const [selected, setSelected] = useState<MediaInputModel>(null);
    const [name, setName] = useState('');
    const [slot, setSlot] = useState('');
    const [bridgeDevice, setBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(null);
    const [control, setControl] = useState<SelectItem<ControlModel>>(null);
    const [valueOptionList, setValueOptionList] = useState<SelectItem<number>[]>([]);
    const [value, setValue] = useState<SelectItem<number>>(null);

    const bridgeDeviceIdList = useMap(useFilterSelector(
        state => state.control.bridgeDeviceList,
        bridgeDeviceItem => bridgeDeviceItem.gateway_id === bridgeDevice?.value.gateway_id,
        [bridgeDevice],
    ), bridgeDeviceItem => bridgeDeviceItem.id);
    const controlOptionList = useMap(useFilterSelector(
        state => state.control.controlList,
        control => bridgeDeviceIdList.indexOf(control.bridge_device_id) !== -1,
        [bridgeDeviceIdList],
    ), control => ({
        value: control,
        label: control.name,
    }));

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const onDeletePressed = useCallback(() => {
        question(t('are_you_sure_to_delete_the_remote'), () => {
            return rpc.deleteMediaInput(selected.id)
                .then(() => {
                    success(t('remote_successfully_deleted'));
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [t, success, failure, onBackPressed, question, rpc, selected]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure(t('pls_enter_a_valid_remote_name'));

            return false;
        }

        if (bridgeDevice == null) {
            failure(t('pls_select_a_valid_device'));

            return false;
        }

        if (control == null) {
            failure(t('pls_select_a_valid_control'));

            return false;
        }

        if (value == null) {
            failure(t('pls_select_a_valid_value'));

            return false;
        }

        return true;
    }, [
        t,
        failure,
        name,
        bridgeDevice,
        control,
        value,
    ]);

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const mediaInput: MediaInputModel = {
            id: selected != null ? selected.id : null,
            name,
            slot: 0,
            bridge_device_id: bridgeDevice.value.id,
            control_id: control.value.id,
            value: value.value,
        };

        return rpc.updateMediaInput(mediaInput)
            .then(() => {
                success(t(selected != null ? 'remote_successfully_edited' : 'remote_successfully_saved'));
                onBackPressed();
            })
            .catch(e => {
                if (e !== 'cant_read') {
                    failure(e);
                }
            });
    }, [
        t,
        isFieldsValid,
        bridgeDevice,
        control,
        success,
        onBackPressed,
        rpc,
        selected,
        failure,
        name,
        value,
    ]);

    const onRegisterSignalPressed = useCallback(() => {
        rpc.registerMediaInput(selected.id)
            .catch(e => {
                if (e !== 'cant_read') {
                    failure(e);
                }
            });
    }, [rpc, selected, failure]);

    const buttons = useMemo(() => [
        {
            text: t('register_signal'),
            className: 'blue',
            onClick: onRegisterSignalPressed,
            hidden: !(selected),
            doNotBlockUI: true,
        },
        {
            text: t('delete_remote'),
            className: 'mulberry-wood',
            onClick: onDeletePressed,
            hidden: !(selected),
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onCreatePressed,
            doNotBlockUI: true,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        },
    ], [t, onRegisterSignalPressed, onCreatePressed, onDeletePressed, selected]);

    const CONTROL_TYPE_VALUE_MAP: Record<ControlRenderType, SelectItem<number>[]> = useMemo(() => ({
        complex: [
            {
                value: 0,
                label: t('close_stop'),
            },
            {
                value: 1,
                label: t('open_up'),
            },
            {
                value: 2,
                label: t('down'),
            },
            {
                value: 3,
                label: t('open_close'),
            },
        ],
        simple: [
            {
                value: 0,
                label: t('close'),
            },
            {
                value: 1,
                label: t('open'),
            },
            {
                value: 3,
                label: t('open_close'),
            },
        ],
        three_phase: [
            {
                value: 0,
                label: t('stop'),
            },
            {
                value: 1,
                label: t('up'),
            },
            {
                value: 2,
                label: t('down'),
            },
        ],
        two_phase: [
            {
                value: 1,
                label: t('up'),
            },
            {
                value: 2,
                label: t('down'),
            },
        ],
        pulse: [
            {
                value: 0,
                label: t('passive'),
            },
            {
                value: 1,
                label: t('active'),
            }],
    }), [t]);

     const switchToHomeMode = useCallback(() => {
         rpc.updateGateway({
             ...isInstallationMode,
             settings: {
                 ...isInstallationMode?.settings,
                 setup_mode: false
             }
         }).catch(failure)
     }, [failure, isInstallationMode, rpc])

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const mediaInput = mediaInputList.find(mediaInputItem => mediaInputItem.id === params.id);
        if (mediaInput == null) {
            return;
        }
        setSelected(mediaInput);

        setName(mediaInput.name);
        setSlot(mediaInput.slot.toString());
        const bridgeDevice = bridgeDeviceOptionList.find(bridgeDeviceOption =>
            bridgeDeviceOption.value.id === mediaInput.bridge_device_id);
        setBridgeDevice(bridgeDevice || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (params.id != null) {
            return;
        }

        setBridgeDevice(bridgeDeviceOptionList[0] || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (control == null) {
            if (selected != null) {
                const controlBefore = controlOptionList.find(control => control.value.id === selected.control_id) ||
                    controlOptionList[0];
                setControl(controlBefore || null);
            } else {
                setControl(controlOptionList[0] || null);
            }
        } else {
            const exists = controlOptionList.some(controlItem => controlItem.value.id === control.value.id);
            if (!exists) {
                setControl(controlOptionList[0] || null);
            }
        }
    }, [selected, control, controlOptionList]);

    useEffect(() => {
        if (control?.value == null) {
            return;
        }

        const controlRenderType = controlTypeSet[control.value.type_id].render_type;
        const list = CONTROL_TYPE_VALUE_MAP[controlRenderType] || [];
        setValueOptionList(list);
        if (selected == null) {
            setValue(list[0] || null);
        } else {
            setValue(list.find(item => item.value === selected.value) || list[0] || null);
        }
    }, [selected, control, controlTypeSet, CONTROL_TYPE_VALUE_MAP]);

    return (
        <PasswordContainer requiredRole='admin' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder={t('write_remote_name')}
                            label={t('remote_name')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        {
                            renderIf(selected)(() =>
                                <>
                                    <SettingReadOnlyRowComponent
                                        title='Slot'
                                        value={slot}/>
                                </>,
                            )
                        }
                        <SettingSelectComponent
                            label={t('device')}
                            options={bridgeDeviceOptionList}
                            onChange={setBridgeDevice}
                            value={bridgeDevice}/>
                        <SettingSelectComponent
                            label={t('control')}
                            options={controlOptionList}
                            onChange={setControl}
                            value={control}/>
                        <SettingSelectComponent
                            label={t('value')}
                            options={valueOptionList}
                            onChange={setValue}
                            value={value}/>
                    </div>
                    <div className='content-wrapper'>
                         <div className="content-wrapper">
                        {renderIf(isInstallationMode?.settings?.setup_mode)(() => (
                            <div className="edit-screen-inside-warning-container">
                                <span>{t('media_page_installation_mode_warning')}</span>
                                <span  {...onClickHandler(switchToHomeMode)}>{t('SWITCH_TO_HOME_MODE')}</span>
                            </div>
                        ))}
                    </div>
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
        </PasswordContainer>
    );
});
